import { Analytics, Control, Player, Pointer } from '@yleisradio/areena-types';
import { useProgressCacheInvalidator } from 'contexts/ProgressCacheInvalidatorContext';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import logger from 'services/logger';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { idFromPointer } from 'utils/pointer';
import { getAvailablePlayerByProgramId } from 'services/areena-api/fetchers';
import { useUILanguage } from 'hooks/useUILanguage';
import { trackEvent } from 'services/yleAnalyticsSdk';
import { toast, toastPlaybackError } from 'components/Notifications';
import { getTranslation } from 'services/translations';
import { useCards } from 'hooks/useCards';
import { useTunnusContext } from '../TunnusContext';
import { usePathname } from 'next/navigation';
import { serviceRoutes } from 'utils/routes';
import { playerToast } from 'components/Player/AudioPlayerWrapper/PlayerToast/PlayerToast';
import { useTranslation } from 'hooks/useTranslation';

// Restore podcast from local storage
// New values are JSON strings (id of the program) but old implementation stored player objects
function usePodcastToRestore(): [
  id: string | null,
  setId: (newId: string | undefined) => void,
] {
  const [podcastToRestore, setPodcastToRestore] = useLocalStorage<
    string | Player | undefined
  >('podcasts:podcastToRestore', undefined);

  let programId: string | null = null;

  try {
    if (typeof podcastToRestore === 'string') {
      programId = podcastToRestore;
    }

    // Old implementation stored player objects
    if (typeof podcastToRestore === 'object' && podcastToRestore !== null) {
      const player = podcastToRestore as Player;
      programId = player.item.id;
    }
  } catch (e) {
    logger.error('Error restoring podcast', e);
  }

  return [programId, setPodcastToRestore];
}

export type ActivePlayer = Player & {
  preventAutoPlay: boolean;
};

type Value = {
  readonly activePlayer: ActivePlayer | undefined;
  readonly setActivePlayer: (player: Player | undefined) => void;
  readonly queue: string[];
  readonly currentlyPlayingId: string | undefined;
  readonly togglePointerInQueue: (control: Control) => void;
  readonly hasNext: boolean;
  readonly hasPrevious: boolean;
  readonly handleOnPlayNext: () => void;
  readonly handleOnPlayPrev: () => void;
  readonly isPointerTargetInQueue: (pointer?: Pointer) => boolean;
  readonly isPlaying: boolean;
  readonly setIsPlaying: (isPlaying: boolean) => void;
  readonly setEntryItemId: (itemId: string) => void;
  readonly setPodcastToRestore: (itemId: string) => void;
  readonly clearPodcastToRestore: () => void;
  readonly openPopover: () => void;
  readonly closePopover: () => void;
  readonly isPopoverOpen: boolean;
  readonly togglePopover: () => void;
};

// Not exported on purpose, use Provider component and hook below
const PlayerStateContext = React.createContext<Value>({
  activePlayer: undefined,
  setActivePlayer: () => {
    logger.warn("ActivePlayerContext doesn't have a Provider");
  },
  queue: [],
  currentlyPlayingId: undefined,
  togglePointerInQueue: () => {
    logger.warn(
      "Can't toggle program to queue because ActivePlayerContext doesn't have a Provider"
    );
  },
  hasNext: false,
  hasPrevious: false,
  handleOnPlayNext: () => {
    logger.warn(
      "Can't get next in queue because ActivePlayerContext doesn't have a Provider"
    );
    return undefined;
  },
  handleOnPlayPrev: () => {
    logger.warn(
      "Can't get previous in queue because ActivePlayerContext doesn't have a Provider"
    );
    return undefined;
  },
  isPointerTargetInQueue: () => {
    logger.warn(
      "Can't get item in queue because ActivePlayerContext doesn't have a Provider"
    );
    return false;
  },
  isPopoverOpen: false,
  openPopover: () => {
    logger.warn(
      "Can't open Popover because ActivePlayerContext doesn't have a Provider"
    );
    return undefined;
  },
  closePopover: () => {
    logger.warn(
      "Can't close popOver because ActivePlayerContext doesn't have a Provider"
    );
    return undefined;
  },
  togglePopover: () => {
    logger.warn(
      "Can't toggle popOver becayse ActivePlayerContext doesn't have a Provider"
    );
    return undefined;
  },
  isPlaying: false,
  setIsPlaying() {
    logger.warn(
      "Can't get item in queue because ActivePlayerContext doesn't have a Provider"
    );
  },
  setEntryItemId() {
    logger.warn(
      "Can't set entry item id because ActivePlayerContext does not have a Provider"
    );
  },
  setPodcastToRestore() {
    logger.warn(
      "Can't update podcast to restore without Provider of ActivePlayerContext"
    );
  },
  clearPodcastToRestore() {
    logger.warn(
      "Can't clear podcast to restore without Provider of the ActivePlayerContext"
    );
  },
});

export const PlayerStateProvider: React.FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [activePlayer, setActivePlayerState] = useState<
    ActivePlayer | undefined
  >();
  const [podcastToRestore, savePodcastToRestore] = usePodcastToRestore();
  const [entryItemId, setEntryItemId] = useState<string | undefined>();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<
    { id: string; isFromDefaultQueue: boolean } | undefined
  >();
  const [isPlaying, setIsPlaying] = useState(false);
  const language = useUILanguage();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [queue, setQueue] = useLocalStorage<string[]>('podcasts:playQueue', []);

  // series-cards + recommendations-cards
  const [queueDefault, setQueueDefault] = useState<string[]>([]);

  const [historyIds, setHistoryIds] = useState<string[]>([]);

  const { invalidate: invalidateUserProgress } = useProgressCacheInvalidator();

  const { isAuthenticated } = useTunnusContext();
  const t = useTranslation();
  const { cards: cardsSeries } = useCards({
    source: activePlayer?.playlistSource,
    pageIndex: 0,
    pageSize: 5,
  });
  const { cards: cardsRecommended } = useCards({
    // don't load recommendations for unauthenticated user: user has no way of seeing recommendations unless logged in
    source: isAuthenticated ? activePlayer?.recommendationsSource : undefined,
    pageIndex: 0,
    pageSize: 5,
  });

  useEffect(
    () => {
      setQueueDefault(
        cardsSeries
          .concat(cardsRecommended)
          .map((c) => idFromPointer(c.pointer))
          .filter((id): id is string => id !== undefined)
      );

      return () => setQueueDefault([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePlayer, cardsSeries.length, cardsRecommended.length]
  );

  const pathname = usePathname();
  const isPodcastPath = pathname.startsWith(
    `/${serviceRoutes['radio'][language]}`
  );

  // Restore podcast from local storage
  useEffect(() => {
    (async () => {
      if (isPodcastPath && podcastToRestore && !activePlayer) {
        const newPlayer = await getAvailablePlayerByProgramId(
          podcastToRestore,
          language
        );

        if (newPlayer) {
          setActivePlayerState({
            ...newPlayer,
            preventAutoPlay: true,
          });
          setCurrentlyPlaying({
            id: newPlayer.item.id,
            isFromDefaultQueue: false,
          });
        } else {
          logger.debug(
            `No available player for id ${podcastToRestore}, removing it from local storage`
          );
          savePodcastToRestore(undefined);
        }
      }
    })();
  }, [
    isPodcastPath,
    podcastToRestore,
    activePlayer,
    language,
    savePodcastToRestore,
  ]);

  const togglePopover = useCallback(() => {
    setIsPopoverOpen((isOpen) => !isOpen);
  }, []);

  const openPopover = useCallback(() => {
    setIsPopoverOpen(true);
  }, []);

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const addCurrentItemToHistory = useCallback(() => {
    if (currentlyPlaying && !historyIds.includes(currentlyPlaying.id)) {
      setHistoryIds((prevState) => [currentlyPlaying.id, ...prevState]);
    }
  }, [currentlyPlaying, historyIds]);

  const popFirstItemFromHistory = useCallback(() => {
    const firstItem = historyIds[0];
    setHistoryIds((prevState) => prevState.slice(1));
    return firstItem;
  }, [historyIds]);

  const setActivePlayer = useCallback(
    (player: Player | undefined, isFromDefaultQueue = false) => {
      // Re-fetch user progress in all lists when the player is closed
      if (!player) {
        invalidateUserProgress();
        setCurrentlyPlaying(undefined);
        setIsPlaying(false);
        setActivePlayerState(undefined);
        return;
      }

      if (player.media.type === 'AudioObject') {
        setCurrentlyPlaying({ id: player.item.id, isFromDefaultQueue });
        setIsPlaying(true);
      }

      setActivePlayerState(
        player ? { ...player, preventAutoPlay: false } : player
      );
    },
    [invalidateUserProgress, setActivePlayerState]
  );

  const isPointerTargetInQueue = useCallback(
    (pointer?: Pointer) => {
      const id = idFromPointer(pointer);
      if (!id) return false;

      return queue.includes(id);
    },
    [queue]
  );

  const fetchPlayer = useCallback(
    async (id: string) => {
      try {
        const playerRes = await getAvailablePlayerByProgramId(id, language);
        if (!playerRes) {
          logger.debug(
            `No available player for program with id ${id}, closing player`
          );
          toastPlaybackError(language);
        }
        return playerRes || undefined;
      } catch (e) {
        setActivePlayer(undefined);
        logger.error('Error fetching player ', e);
        toastPlaybackError(language);
      }
    },
    [language, setActivePlayer]
  );

  const toggleIdInQueue = useCallback(
    async (id: string, analytics?: Analytics) => {
      // Open player if user is adding their first item to queue list and player is closed.
      if (queue.length === 0 && activePlayer === undefined) {
        trackEvent('areena.play-queue.add', null, analytics?.context?.comscore);
        void playerToast(t('addedToQueue'));

        const playerRes = await fetchPlayer(id);
        if (playerRes !== undefined) {
          setCurrentlyPlaying({ id, isFromDefaultQueue: false });
          setIsPlaying(false);
          setActivePlayerState({
            ...playerRes,
            preventAutoPlay: true,
          });
        }
        // Don't add the freshly fetched item to queue
        return;
      }

      setQueue((prevState) => {
        if (prevState.includes(id)) {
          trackEvent(
            'areena.play-queue.remove',
            null,
            analytics?.context?.comscore
          );
          playerToast(t('removedFromQueue'));
          return prevState.filter((item) => item !== id);
        } else {
          trackEvent(
            'areena.play-queue.add',
            null,
            analytics?.context?.comscore
          );
          playerToast(t('addedToQueue'));
          return [...prevState, id];
        }
      });
    },
    [setQueue, activePlayer, queue, fetchPlayer, t]
  );

  const togglePointerInQueue = useCallback(
    (control?: Control) => {
      const id = idFromPointer(control?.destination);

      if (!id) {
        void toast(
          getTranslation('queueToggleError', language),
          'error',
          getTranslation('notificationHelpTextGeneric', language)
        );

        throw new Error(
          "Couldn't get id from pointer when trying to toggle item in queue"
        );
      }
      void toggleIdInQueue(id, control?.analytics);
    },
    [toggleIdInQueue, language]
  );

  const addIdToFirstInQueue = useCallback(
    (itemId: string) => {
      setQueue((prevState) =>
        prevState[0] !== itemId
          ? [itemId, ...prevState.filter((item) => item !== itemId)]
          : prevState
      );
    },
    [setQueue]
  );

  const hasNext = queue.length > 0 || queueDefault.length > 0;

  const hasPrevious =
    currentlyPlaying?.id !== entryItemId && historyIds.length > 0;

  const handleOnPlayNext = useCallback(async () => {
    const nextId = queue[0] ?? queueDefault[0];

    if (!nextId) return;

    const playerRes = await fetchPlayer(nextId);

    if (playerRes !== undefined) {
      const isFromUserQueue = Boolean(queue.length);
      setActivePlayer(playerRes, !isFromUserQueue);

      addCurrentItemToHistory();

      setQueue((prevState) => prevState.slice(1));
    }
  }, [
    fetchPlayer,
    queue,
    setActivePlayer,
    addCurrentItemToHistory,
    setQueue,
    queueDefault,
  ]);

  const handleOnPlayPrev = useCallback(async () => {
    if (currentlyPlaying?.id === entryItemId) return;

    const prevId = popFirstItemFromHistory();

    if (!prevId) return;

    const playerRes = await fetchPlayer(prevId);

    if (playerRes !== undefined) {
      setActivePlayer(playerRes);

      if (
        currentlyPlaying !== undefined &&
        !currentlyPlaying.isFromDefaultQueue
      ) {
        addIdToFirstInQueue(currentlyPlaying.id);
      }
    }
  }, [
    fetchPlayer,
    setActivePlayer,
    currentlyPlaying,
    entryItemId,
    addIdToFirstInQueue,
    popFirstItemFromHistory,
  ]);

  const setPodcastToRestore = useCallback(
    (id: string) => {
      if (!isPodcastPath) return;

      savePodcastToRestore(id);
    },
    [isPodcastPath, savePodcastToRestore]
  );

  const clearPodcastToRestore = useCallback(() => {
    if (!isPodcastPath) return;

    savePodcastToRestore(undefined);
  }, [savePodcastToRestore, isPodcastPath]);

  const value = useMemo<Value>(
    () => ({
      activePlayer,
      setActivePlayer,
      queue,
      currentlyPlayingId: currentlyPlaying?.id,
      togglePointerInQueue,
      hasNext,
      hasPrevious,
      handleOnPlayNext,
      handleOnPlayPrev,
      isPointerTargetInQueue,
      isPlaying,
      setIsPlaying,
      setEntryItemId,
      setPodcastToRestore,
      clearPodcastToRestore,
      openPopover,
      closePopover,
      isPopoverOpen,
      togglePopover,
    }),
    [
      activePlayer,
      setActivePlayer,
      queue,
      currentlyPlaying,
      togglePointerInQueue,
      hasNext,
      hasPrevious,
      handleOnPlayNext,
      handleOnPlayPrev,
      isPointerTargetInQueue,
      isPlaying,
      setPodcastToRestore,
      clearPodcastToRestore,
      openPopover,
      closePopover,
      isPopoverOpen,
      togglePopover,
    ]
  );

  return (
    <PlayerStateContext.Provider value={value}>
      {children}
    </PlayerStateContext.Provider>
  );
};

export function usePlayerState(): Value {
  return useContext(PlayerStateContext);
}
